import React, { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "react-query";

import * as THREE from "three";
import useProjectEditAccess from "@/services/context/project-process/use-project-edit-access";

import {
    ModelingMeasureRepresentation,
    ModelingType,
} from "@/services/context/viewer/modeling-tools/modeling-tools-context-provider.tsx";
import useProjectContext from "@/services/context/project/use-project-context.ts";
import PlanOrigin from "../project-organisation/models/plan-origin.ts";

import ProjectMeasures from "./models/project-measures.ts";
import BuildingMeasures from "./models/building-measures.ts";
import StoreyMeasures from "./models/storey-measures.ts";
import Measure from "./models/measure.ts";
import MeasureType from "./models/measure-type.ts";
import MeasureShape from "./models/measure-shape.ts";
import MeasureRepresentation from "./models/measure-representation.ts";
import FileRecord from "./models/file-record.ts";
import FragmentRecord from "./models/fragment-record.ts";
import PlanFileData from "./models/plan-file-data.ts";

import MeasureColors from "@/models/enums/measure-colors.ts";
import MeasureRepresentationGuids from "@/models/enums/measure-representation-guids.ts";
import ProjectMeasureContextProps, { ProjectMeasureContext } from "./project-measure-context-props";
import { deleteItem, fetchItem, postItem, putItem } from "@/queries/queries-helper.ts";
import ENDPOINTS from "@/services/endpoints.ts";
import MEASURE_ENDPOINTS from "@/services/endpoints/measure-endpoints.ts";
import ProjectMeasuresDto from "@/models/interfaces/dtos/measure/get/project-measures-dto.ts";
import MeasureTypeDto from "@/models/interfaces/dtos/measure/get/measure-type-dto.ts";
import CreateMeasureTypeDto from "@/models/interfaces/dtos/measure/create/create-measure-type-dto.ts";
import UpdateMeasureTypeDto from "@/models/interfaces/dtos/measure/update/update-measure-type-dto.ts";
import MeasureShapeDto from "@/models/interfaces/dtos/measure/get/measure-shape-dto.ts";
import { BaamerFileRevisionDto } from "@/models/interfaces/dtos/baamer-file-revision-dto.ts";
import PathElementDto from "@/models/interfaces/dtos/path-element-dto.ts";
import SimpleLoadingIndicator from "@/components/simple-loading-indicator";
import KnownScaleContent from "../project-organisation/models/known-scale-content.ts";
import ResponseDTO from "@/models/interfaces/dtos/response-dto.ts";
import MeasureShapesType from "@/models/enums/measure-shapes.ts";
import createProjectMeasuresFromDto from "@/pages/takeoff/context/project-measure/create-project-measures-from-dto.tsx";

const ProjectMeasureProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const { ensureCanEdit } = useProjectEditAccess();
    const { project } = useProjectContext();

    const queryClient = useQueryClient();
    const [projectMeasures, setProjectMeasures] = useState<ProjectMeasures | undefined>(undefined);

    const [storeyFiles, setStoreyFiles] = useState<Map<string, FileRecord[]> | undefined>(undefined);
    const [fragsData, setFragsData] = useState<Map<string, FragmentRecord[]> | undefined>(undefined);
    const [buildingFiles, setBuildingFiles] = useState<Map<string, FileRecord[]> | undefined>(undefined);

    // Liste temporaire des types de métré édités, en attente d'un envoi à l'API lors de la sauvegarde globale.
    const tempMeasureTypesRef = useRef<MeasureType[]>([]);

    // Liste temporaire des IDs de types de métré à supprimer, en attente d'un envoi à l'API lors de la sauvegarde globale.
    const deletedMeasureTypeIdsRef = useRef<string[]>([]);

    // -- DATA INITIALISATION -- //
    const { data: projectMeasuresDto, isLoading: isProjectMeasureLoading } = useQuery(
        ["project-measures", project.id],
        async () => fetchItem<ProjectMeasuresDto>(MEASURE_ENDPOINTS.GET_PROJECT_MEASURES(project.id)),
        {
            refetchOnWindowFocus: false,
            // Le calcul peut remplacer les fichiers de géométrie sans modifier ce DTO.
            // Recréer les métrés et leurs loaders après chaque lecture réussie.
            structuralSharing: false,
        },
    );

    const { data: baamerFileRevisions, isLoading: isBaamerFileRevisions } = useQuery(
        ["project-baamer-file-revisions", project.id],
        async () => fetchItem<BaamerFileRevisionDto[]>(ENDPOINTS.FILE_REVISION_BY_PROJECT_ID(project.id)),
        {
            refetchOnWindowFocus: false,
        },
    );

    const { data: projectMeasureTypes, isLoading: isProjectTypesLoading } = useQuery(
        ["project-measure-types", project.id],
        async () => {
            const data = await fetchItem<MeasureTypeDto[]>(
                MEASURE_ENDPOINTS.GET_PROJECT_MEASURE_TYPES(project.id),
            );
            if (data == null) return undefined;
            const measureTypes: MeasureType[] = [];
            data.forEach((dto) => {
                const { measureRepresentation } = dto;
                const parsedInformations = dto.dataInformations
                    ? JSON.parse(dto.dataInformations)
                    : undefined;
                const measureType = new MeasureType(
                    dto.measureTypeId,
                    dto.measureRepresentationId,
                    measureRepresentation.measureShapeId,
                    dto.name,
                    parsedInformations,
                );
                measureTypes.push(measureType);
            });

            return measureTypes;
        },
        { refetchOnWindowFocus: false },
    );

    const clearTempData = useCallback(() => {
        tempMeasureTypesRef.current = [];
        deletedMeasureTypeIdsRef.current = [];
    }, []);

    const invalidateMeasureTypes = useCallback(async () => {
        clearTempData();
        await queryClient.invalidateQueries({ queryKey: ["project-measure-types", project.id] });
    }, [queryClient, project.id, clearTempData]);

    const refreshData = useCallback(async () => {
        clearTempData();
        const updatedDto = await fetchItem<ProjectMeasuresDto>(
            MEASURE_ENDPOINTS.GET_PROJECT_MEASURES(project.id),
        );
        if (updatedDto) {
            queryClient.setQueryData(["project-measures", project.id], updatedDto);
            setProjectMeasures(createProjectMeasuresFromDto(updatedDto));
        }
    }, [queryClient, project.id, clearTempData]);

    const completeRefreshData = useCallback(async () => {
        setStoreyFiles(undefined);
        setFragsData(undefined);
        setBuildingFiles(undefined);
        clearTempData();
        await queryClient.resetQueries(["project-baamer-file-revisions", project.id]);
        await queryClient.resetQueries(["project-measures", project.id]);
    }, [project.id, queryClient, clearTempData]);

    const prepareSitePosition = useCallback((_data: string | undefined): THREE.Vector3 | undefined => {
        if (!_data) return undefined;
        try {
            const parsedData = JSON.parse(_data);
            const sitePosition = parsedData.sitePosition || parsedData.SitePosition;
            if (sitePosition && typeof sitePosition === "object")
                return new THREE.Vector3(
                    sitePosition.x || sitePosition.X,
                    sitePosition.y || sitePosition.Y,
                    sitePosition.z || sitePosition.Z,
                );
        } catch {
            // Intentionally ignore invalid JSON
        }
        return undefined;
    }, []);

    // Contient uniquement les representations de mesure les familles enfants.
    const { data: availableShapes, isLoading: isLoadingAllRepresentations } = useQuery(
        ["measure-representations"],
        async () => {
            const data = await fetchItem<MeasureShapeDto[]>(MEASURE_ENDPOINTS.GET_REPRESENTATIONS());
            if (data == null) return undefined;

            const measureRepresentations: MeasureShape[] = [];
            data.forEach((shapeDto) => {
                const shape = new MeasureShape(shapeDto.measureShapeId, shapeDto.name as MeasureShapesType);

                shapeDto.measureRepresentations.forEach((reprensentationDto) => {
                    const representation = new MeasureRepresentation(
                        reprensentationDto.measureRepresentationId,
                        reprensentationDto.measureShapeId,
                        reprensentationDto.name,
                    );

                    reprensentationDto.measureTypes.forEach((measureTypeDto) => {
                        let dataInformations: Record<string, unknown> | undefined;
                        if (measureTypeDto.dataInformations) {
                            try {
                                dataInformations = JSON.parse(measureTypeDto.dataInformations);
                            } catch {
                                // Intentionally ignore invalid JSON
                            }
                        }

                        const type = new MeasureType(
                            measureTypeDto.measureTypeId,
                            measureTypeDto.measureRepresentation.measureRepresentationId,
                            measureTypeDto.measureRepresentation.measureShapeId,
                            measureTypeDto.name,
                            dataInformations,
                        );
                        representation.measureTypes.push(type);
                    });
                    shape.representations.push(representation);
                });
                measureRepresentations.push(shape);
            });

            return measureRepresentations;
        },
        { refetchOnWindowFocus: false, staleTime: 1000 * 60 * 60 },
    );

    useEffect(() => {
        if (baamerFileRevisions == null || projectMeasuresDto == null) return undefined;
        let isCancelled = false;

        const loadFiles = async () => {
            const storeyMap = new Map<string, Array<FileRecord>>();
            const storeyFrags = new Map<string, Array<FragmentRecord>>();
            const buildingMap = new Map<string, Array<FileRecord>>();
            const buildings = projectMeasuresDto?.buildings ?? [];

            const promises = baamerFileRevisions.flatMap((bamfr) => {
                const storeyPromises = bamfr.storeyFileRevisions.map(async (sfr) => {
                    const { storeyId } = sfr;

                    let parsedData: Record<string, unknown> = {};
                    let parsedData1: Record<string, unknown> = {};

                    if (sfr.data) {
                        try {
                            parsedData = JSON.parse(sfr.data) as Record<string, unknown>;
                        } catch {
                            // Intentionally ignore invalid JSON
                        }
                    }

                    if (bamfr.data) {
                        try {
                            parsedData1 = JSON.parse(bamfr.data) as Record<string, unknown>;
                        } catch {
                            // Intentionally ignore invalid JSON
                        }
                    }

                    const planFileData = PlanFileData.fromJSON({
                        ...parsedData,
                        ...parsedData1,
                    });

                    bamfr.origins
                        .filter((o) => o.baamerFileRevisionId === sfr.baamerFileRevisionId)
                        .forEach((o) => {
                            try {
                                const planOrigin = new PlanOrigin();

                                planOrigin.setId(o.originId);
                                planOrigin.setName(o.name);
                                planOrigin.setColor(o.color);
                                planOrigin.setIsActive(o.isActive);
                                planOrigin.setPositionFromJSON(JSON.parse(o.position));
                                planOrigin.setDirectionFromJSON(JSON.parse(o.direction));

                                planFileData.origins.push(planOrigin);
                            } catch {
                                // Intentionally ignore invalid JSON
                            }
                        });

                    const { name, fileId, fileType } = bamfr.fileRevision.file;

                    const planFileDataContent = new KnownScaleContent();
                    planFileDataContent.default = true;

                    const fileRevision = new FileRecord(
                        bamfr.fileRevisionId,
                        fileId,
                        name,
                        fileType,
                        bamfr.fileRevision.relativePath,
                        planFileData.origin,
                        planFileData.content ?? planFileDataContent,
                        planFileData.sitePosition,
                    );

                    const mapItem = storeyMap.get(storeyId);

                    if (mapItem == null) {
                        storeyMap.set(storeyId, [fileRevision]);
                    } else {
                        mapItem.push(fileRevision);
                    }

                    const buildingId = buildings.find(
                        (b) => b.storeys.find((s) => s.storeyId === storeyId) != null,
                    )?.buildingId;

                    const storeyFragments =
                        (await fetchItem<PathElementDto[]>(
                            ENDPOINTS.PROJECT_MEASURE_3D_FILES(
                                project.id,
                                bamfr.baamerRevisionId,
                                buildingId,
                                storeyId,
                            ),
                        )) ?? [];

                    storeyFragments.forEach((fragData) => {
                        const fileFrag = new FragmentRecord(
                            fragData.name,
                            fragData.path,
                            planFileData.origin,
                            planFileData.sitePosition,
                        );

                        const storeyFragmentsMapItem = storeyFrags.get(storeyId);

                        if (storeyFragmentsMapItem == null) {
                            storeyFrags.set(storeyId, [fileFrag]);
                        } else {
                            storeyFragmentsMapItem.push(fileFrag);
                        }
                    });

                    return fileRevision;
                });

                const buildingPromises = bamfr.buildingFileRevisions.map(async (bfr) => {
                    const { buildingId } = bfr;

                    const { name, fileId, fileType } = bamfr.fileRevision.file;

                    const planFileDataContent = new KnownScaleContent();
                    planFileDataContent.default = true;

                    const fileRevision = new FileRecord(
                        bamfr.fileRevisionId,
                        fileId,
                        name,
                        fileType,
                        bamfr.fileRevision.relativePath,
                        PlanOrigin.getDefault(),
                        planFileDataContent,
                        prepareSitePosition(bamfr.data),
                    );

                    const mapItem = buildingMap.get(buildingId);

                    if (mapItem == null) {
                        buildingMap.set(buildingId, [fileRevision]);
                    } else {
                        mapItem.push(fileRevision);
                    }

                    return fileRevision;
                });

                return [...storeyPromises, ...buildingPromises];
            });

            await Promise.all(promises);
            if (isCancelled) return;
            setStoreyFiles(storeyMap);
            setFragsData(storeyFrags);
            setBuildingFiles(buildingMap);
        };

        loadFiles();
        return () => {
            isCancelled = true;
        };
    }, [baamerFileRevisions, project.id, projectMeasuresDto, prepareSitePosition]);

    useEffect(() => {
        if (projectMeasuresDto == null) return;
        setProjectMeasures(createProjectMeasuresFromDto(projectMeasuresDto));
    }, [projectMeasuresDto]);

    // -- METHODS -- //
    const addMeasureType = useCallback(
        async (measureRepresentationId: string, name: string, dataInformations: string) => {
            if (!ensureCanEdit()) {
                return null;
            }

            const updateData: CreateMeasureTypeDto = {
                measureRepresentationId: measureRepresentationId,
                name,
                dataInformations,
            };
            const result = await postItem<ResponseDTO<MeasureTypeDto>>(
                MEASURE_ENDPOINTS.ADD_MEASURE_TYPE(project.id),
                updateData,
            );
            if (!(result && result.success)) return null;

            const addedType = result.entity;
            const { measureRepresentation } = addedType;
            const parsedInformations = addedType.dataInformations
                ? JSON.parse(addedType.dataInformations)
                : undefined;
            const type = new MeasureType(
                addedType.measureTypeId,
                addedType.measureRepresentationId,
                measureRepresentation.measureShapeId,
                addedType.name,
                parsedInformations,
            );

            const projectMeasuresTypes = queryClient.getQueryData<MeasureType[]>([
                "project-measure-types",
                project.id,
            ]);
            if (projectMeasuresTypes)
                queryClient.setQueryData(
                    ["project-measure-types", project.id],
                    [...projectMeasuresTypes, type],
                );
            else await queryClient.invalidateQueries(["project-measure-types", project.id]);

            return type;
        },
        [ensureCanEdit, project.id, queryClient],
    );

    const editMeasureTypeLocally = useCallback((updatedType: MeasureType) => {
        if (!ensureCanEdit()) {
            return;
        }

        // On ajoute (ou remplace) le type dans la liste temporaire, envoyée uniquement lors de la sauvegarde globale.
        const alreadyEdited = tempMeasureTypesRef.current.some(
            (t) => t.measureTypeId === updatedType.measureTypeId,
        );
        tempMeasureTypesRef.current = alreadyEdited
            ? tempMeasureTypesRef.current.map((t) =>
                  t.measureTypeId === updatedType.measureTypeId ? updatedType : t,
              )
            : [...tempMeasureTypesRef.current, updatedType];
    }, [ensureCanEdit]);

    const markMeasureTypeForDeletion = useCallback((measureTypeId: string) => {
        if (!ensureCanEdit()) {
            return;
        }

        if (!deletedMeasureTypeIdsRef.current.includes(measureTypeId)) {
            deletedMeasureTypeIdsRef.current = [...deletedMeasureTypeIdsRef.current, measureTypeId];
        }
        tempMeasureTypesRef.current = tempMeasureTypesRef.current.filter(
            (t) => t.measureTypeId !== measureTypeId,
        );
    }, [ensureCanEdit]);

    const saveMeasureTypes = useCallback(async (): Promise<boolean> => {
        if (!ensureCanEdit()) {
            return false;
        }

        const toSave = tempMeasureTypesRef.current;
        if (toSave.length === 0) return true;

        const updateData: UpdateMeasureTypeDto[] = toSave.map((type) => ({
            measureTypeId: type.measureTypeId,
            measureRepresentationId: type.measureRepresentationId,
            name: type.name,
            dataInformations: type.dataInformations ? JSON.stringify(type.dataInformations) : undefined,
        }));

        try {
            const result = await putItem<ResponseDTO<MeasureTypeDto[]>>(
                MEASURE_ENDPOINTS.UPDATE_MEASURE_TYPES(project.id),
                updateData,
            );
            if (!result?.success) return false;

            queryClient.setQueryData<MeasureType[] | undefined>(
                ["project-measure-types", project.id],
                (previousTypes) => {
                    if (previousTypes == null) return previousTypes;

                    return previousTypes.map((type) => {
                        const updatedType = toSave.find((t) => t.measureTypeId === type.measureTypeId);
                        return updatedType ?? type;
                    });
                },
            );

            return true;
        } catch {
            return false;
        }
    }, [ensureCanEdit, project.id, queryClient]);

    const deleteMeasureTypes = useCallback(async (): Promise<boolean> => {
        if (!ensureCanEdit()) {
            return false;
        }

        const toDelete = deletedMeasureTypeIdsRef.current;
        if (toDelete.length === 0) return true;

        try {
            await deleteItem(MEASURE_ENDPOINTS.DELETE_MEASURE_TYPES(project.id), toDelete);
            queryClient.setQueryData<MeasureType[] | undefined>(
                ["project-measure-types", project.id],
                (previousTypes) => {
                    if (previousTypes == null) return previousTypes;
                    return previousTypes.filter((type) => !toDelete.includes(type.measureTypeId));
                },
            );
            await refreshData();
            return true;
        } catch {
            return false;
        }
    }, [ensureCanEdit, project.id, queryClient, refreshData]);

    const deleteMeasuresFromMeasureTypesAndStorey = useCallback(
        async (storeyId: string): Promise<boolean> => {
            if (!ensureCanEdit()) {
                return false;
            }

            const toDelete = deletedMeasureTypeIdsRef.current;
            if (toDelete.length === 0) return true;

            try {
                await deleteItem(
                    MEASURE_ENDPOINTS.DELETE_MEASURE_FROM_MEASURE_TYPES_AND_STOREY(project.id, storeyId),
                    toDelete,
                );
                return true;
            } catch {
                return false;
            }
        },
        [ensureCanEdit, project.id],
    );

    const updateProjectMeasuresFromMeasuresTypes = useCallback(
        async (excludeStoreyIds: string[], measureTypeIds: string[]): Promise<boolean> => {
            if (!ensureCanEdit()) {
                return false;
            }

            if (measureTypeIds.length === 0) return true;

            try {
                await putItem(
                    MEASURE_ENDPOINTS.UPDATE_PROJECT_MEASURES_FROM_MEASURES_TYPES(
                        project.id,
                        excludeStoreyIds,
                    ),
                    measureTypeIds,
                );
                await refreshData();
                return true;
            } catch {
                return false;
            }
        },
        [ensureCanEdit, project.id, refreshData],
    );

    const updateMeasuresAndRecords = useCallback(
        (
            buildingId: string,
            storeyId: string,
            updatedMeasures: Measure[],
            updatedFileRecords: FileRecord[],
            updatedFragData: FragmentRecord[] | undefined,
        ) => {
            setProjectMeasures((prevMeasures) => {
                if (!prevMeasures) return prevMeasures;
                const targetStorey = prevMeasures.getStorey(buildingId, storeyId);
                if (!targetStorey) return prevMeasures;

                const newProjectMeasures = new ProjectMeasures();
                newProjectMeasures.buildings = prevMeasures.buildings.map((b) => {
                    if (b.id !== buildingId) return b;
                    const newBuilding = new BuildingMeasures(b.id, b.name);
                    newBuilding.storeys = b.storeys.map((s) => {
                        if (s.id !== storeyId) return s;
                        const newStorey = new StoreyMeasures(s.id, s.name, s.index, s.altimetry);
                        newStorey.measures = updatedMeasures;
                        return newStorey;
                    });
                    return newBuilding;
                });
                return newProjectMeasures;
            });

            setStoreyFiles((prevStoreyFiles) => {
                if (!prevStoreyFiles) return prevStoreyFiles;

                const newStoreyFiles = new Map(prevStoreyFiles);
                newStoreyFiles.set(storeyId, updatedFileRecords);
                return newStoreyFiles;
            });

            if (updatedFragData != null) {
                setFragsData((prevFragsData) => {
                    if (!prevFragsData) return prevFragsData;

                    const newStoreyFragsData = new Map(prevFragsData);
                    newStoreyFragsData.set(storeyId, updatedFragData);
                    return newStoreyFragsData;
                });
            }

            setBuildingFiles((prevBuildingFiles) => {
                if (!prevBuildingFiles) return prevBuildingFiles;

                const newBuildingFiles = new Map(prevBuildingFiles);
                newBuildingFiles.set(buildingId, updatedFileRecords);
                return newBuildingFiles;
            });
        },
        [],
    );

    const getMeasureType = useCallback(
        (measureTypeId: string) =>
            tempMeasureTypesRef.current.find((t) => t.measureTypeId === measureTypeId) ??
            projectMeasureTypes?.find((t) => t.measureTypeId === measureTypeId),
        [projectMeasureTypes],
    );

    const getDrawingTypeFromMeasureTypeId = useCallback(
        (measureTypeId: string) => {
            const shapeId = projectMeasureTypes?.find(
                (t) => t.measureTypeId === measureTypeId,
            )?.measureShapeId;
            const shapeName = availableShapes?.find((s) => s.measureShapeId === shapeId)?.name;
            if (shapeName == null) return ModelingType.Line;
            switch (shapeName) {
                case "Point":
                    return ModelingType.Point;
                case "Line":
                    return ModelingType.Line;
                case "Polyline":
                    return ModelingType.Polyline;
                case "Surface":
                    return ModelingType.Surface;
                default:
                    return ModelingType.Line;
            }
        },
        [availableShapes, projectMeasureTypes],
    );

    const getDrawingTypeFromShapeId = useCallback(
        (shapeId: string) => {
            const shape = availableShapes?.find((s) => s.measureShapeId === shapeId);
            if (shape == null) return ModelingType.Line;
            switch (shape.name) {
                case "Point":
                    return ModelingType.Point;
                case "Line":
                    return ModelingType.Line;
                case "Polyline":
                    return ModelingType.Polyline;
                case "Surface":
                    return ModelingType.Surface;
                default:
                    return ModelingType.Line;
            }
        },
        [availableShapes],
    );

    const getDrawingMeasureRepresentation = useCallback((measureType: MeasureType) => {
        switch (measureType.measureRepresentationId.toLowerCase()) {
            case MeasureRepresentationGuids.LINE_VOLUME_GUID.toLowerCase():
                return ModelingMeasureRepresentation.LineVolume;
            case MeasureRepresentationGuids.LINE_IMPRINT_GUID.toLowerCase():
                return ModelingMeasureRepresentation.LineImprint;
            case MeasureRepresentationGuids.POLYLINE_PANEL_GUID.toLowerCase():
                return ModelingMeasureRepresentation.PolylinePanel;
            case MeasureRepresentationGuids.EXTRUSION_ROOM_GUID.toLowerCase():
                return ModelingMeasureRepresentation.ExtrusionRoom;
            case MeasureRepresentationGuids.EXTRUSION_GUID.toLowerCase():
                return ModelingMeasureRepresentation.Extrusion;
            case MeasureRepresentationGuids.LINE_VOLUME_OFFSET_GUID.toLowerCase():
                return ModelingMeasureRepresentation.LineVolumeOffset;
            case MeasureRepresentationGuids.LINE_VOLUME_DOOR_GUID.toLowerCase():
                return ModelingMeasureRepresentation.LineVolumeDoor;
            case MeasureRepresentationGuids.FINE_SURFACE_GUID.toLowerCase():
                return ModelingMeasureRepresentation.FineSurface;
            case MeasureRepresentationGuids.DISK_GUID.toLowerCase():
                return ModelingMeasureRepresentation.Disk;
            default:
                return ModelingMeasureRepresentation.LineImprint;
        }
    }, []);

    const getDrawingMeasureRepresentationFromMeasureTypeId = useCallback(
        (measureTypeId: string) => {
            const measureType = getMeasureType(measureTypeId);
            if (measureType == null) return ModelingMeasureRepresentation.LineImprint; // Par défaut LineImprint (Mur, Fênetre, Porte)
            return getDrawingMeasureRepresentation(measureType);
        },
        [getMeasureType, getDrawingMeasureRepresentation],
    );

    const getDrawingColorFromMeasureTypeId = useCallback(
        (measureTypeId: string) => {
            const measureType = getMeasureType(measureTypeId);
            if (measureType?.dataInformations != null && measureType?.dataInformations.color != null)
                return new THREE.Color(measureType?.dataInformations.color as string);
            return new THREE.Color(MeasureColors.Wall); // Par défaut la couleur mur
        },
        [getMeasureType],
    );

    const getDrawingAlphaFromMeasureTypeId = useCallback(
        (measureTypeId: string) => {
            const measureType = getMeasureType(measureTypeId);
            if (measureType?.dataInformations != null && measureType?.dataInformations.alpha != null) {
                const { alpha } = measureType.dataInformations;
                if (typeof alpha === "string") return parseFloat(alpha);
                return alpha as number;
            }
            return 1; // Par défaut alpha = 1 pour que la couleur soit opaque
        },
        [getMeasureType],
    );

    const getDrawingDataInformationsFromMeasureTypeId = useCallback(
        (measureTypeId: string) => {
            const measureType = getMeasureType(measureTypeId);
            return measureType?.dataInformations;
        },
        [getMeasureType],
    );

    const isLoading =
        isProjectMeasureLoading ||
        isProjectTypesLoading ||
        isLoadingAllRepresentations ||
        isBaamerFileRevisions ||
        projectMeasures == null ||
        projectMeasureTypes == null ||
        availableShapes == null ||
        baamerFileRevisions == null ||
        fragsData == null ||
        storeyFiles == null ||
        buildingFiles == null;

    if (isLoading) return <SimpleLoadingIndicator />;

    const contextProps: ProjectMeasureContextProps = {
        projectMeasures,
        baamerFileRevisions,
        storeysFileData: storeyFiles,
        storeysFragsData: fragsData,
        buildingsFileData: buildingFiles,
        projectMeasureTypes,
        predefinedShapes: availableShapes,
        get tempMeasureTypes() {
            return tempMeasureTypesRef.current;
        },
        get deletedMeasureTypeIds() {
            return deletedMeasureTypeIdsRef.current;
        },
        isLoading,

        addMeasureType,
        getMeasureType,
        editMeasureTypeLocally,
        saveMeasureTypes,
        markMeasureTypeForDeletion,
        deleteMeasureTypes,
        deleteMeasuresFromMeasureTypesAndStorey,
        updateProjectMeasuresFromMeasuresTypes,
        updateMeasuresAndRecords,

        getDrawingTypeFromMeasureTypeId,
        getDrawingTypeFromShapeId,
        getDrawingMeasureRepresentation,
        getDrawingMeasureRepresentationFromMeasureTypeId,
        getDrawingColorFromMeasureTypeId,
        getDrawingAlphaFromMeasureTypeId,
        getDrawingDataInformationsFromMeasureTypeId,

        completeRefreshData,
        refreshData,
        clearTempData,
        invalidateMeasureTypes,
    };

    return <ProjectMeasureContext.Provider value={contextProps}>{children}</ProjectMeasureContext.Provider>;
};

export default ProjectMeasureProvider;
